import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioAccommodationAddonObject from '@/entities/ratioAccommodationAddon/interface'
import ratioPersonalAddonObject from '@/entities/ratioPersonalAddon/interface'
import ratioBookingAddonObject from '@/entities/ratioBookingAddon/interface'
import ratioTravelExtensionObject from '@/entities/ratioTravelExtension/interface'

import {
  ALL_TRAVELLERS_QUERY,
  SINGLE_TRAVELLER_QUERY,
  TRAVELLERS_DEPENDENTS_QUERY,
  CREATE_TRAVELLER_MUTATION,
  UPDATE_TRAVELLER_MUTATION,
  DELETE_TRAVELLER_MUTATION,
  FORCE_DELETE_TRAVELLER_MUTATION,
  ADD_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION,
  REMOVE_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION,
  CREATE_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION,
  DELETE_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION,
  UPDATE_TRAVELLER_ACCOMMODATION_ADDONS_ORDER_MUTATION,
  ADD_TRAVELLER_PERSONAL_ADDONS_MUTATION,
  REMOVE_TRAVELLER_PERSONAL_ADDONS_MUTATION,
  CREATE_TRAVELLER_PERSONAL_ADDONS_MUTATION,
  DELETE_TRAVELLER_PERSONAL_ADDONS_MUTATION,
  UPDATE_TRAVELLER_PERSONAL_ADDONS_ORDER_MUTATION,
  ADD_TRAVELLER_BOOKING_ADDONS_MUTATION,
  REMOVE_TRAVELLER_BOOKING_ADDONS_MUTATION,
  CREATE_TRAVELLER_BOOKING_ADDONS_MUTATION,
  DELETE_TRAVELLER_BOOKING_ADDONS_MUTATION,
  UPDATE_TRAVELLER_BOOKING_ADDONS_ORDER_MUTATION,
  ADD_TRAVELLER_EXTENSIONS_MUTATION,
  REMOVE_TRAVELLER_EXTENSIONS_MUTATION,
  CREATE_TRAVELLER_EXTENSIONS_MUTATION,
  DELETE_TRAVELLER_EXTENSIONS_MUTATION,
  UPDATE_TRAVELLER_EXTENSIONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const travellerRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_TRAVELLER_QUERY, variables).then((data: any) => {
      return hydrateObject(data.traveller)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_TRAVELLERS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.travellers)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_TRAVELLER_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.traveller),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_TRAVELLERS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.travellers),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(TRAVELLERS_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([TRAVELLERS_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_TRAVELLER_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.traveller)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_TRAVELLER_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.traveller)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_TRAVELLER_MUTATION, variables).then((data: any) => {
      return data.deleteTraveller
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_TRAVELLER_MUTATION, variables).then((data: any) => {
      return data.forceDeleteTraveller
    })
  },

  assignAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  unassignAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  createAccommodationAddons: async (id: string, items: Array<ratioAccommodationAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  deleteAccommodationAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVELLER_ACCOMMODATION_ADDONS_MUTATION, variables)
  },

  updateAccommodationAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        accommodationAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVELLER_ACCOMMODATION_ADDONS_ORDER_MUTATION, variables)
  },

  assignPersonalAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVELLER_PERSONAL_ADDONS_MUTATION, variables)
  },

  unassignPersonalAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVELLER_PERSONAL_ADDONS_MUTATION, variables)
  },

  createPersonalAddons: async (id: string, items: Array<ratioPersonalAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVELLER_PERSONAL_ADDONS_MUTATION, variables)
  },

  deletePersonalAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVELLER_PERSONAL_ADDONS_MUTATION, variables)
  },

  updatePersonalAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        personalAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVELLER_PERSONAL_ADDONS_ORDER_MUTATION, variables)
  },

  assignBookingAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVELLER_BOOKING_ADDONS_MUTATION, variables)
  },

  unassignBookingAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVELLER_BOOKING_ADDONS_MUTATION, variables)
  },

  createBookingAddons: async (id: string, items: Array<ratioBookingAddonObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddons: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVELLER_BOOKING_ADDONS_MUTATION, variables)
  },

  deleteBookingAddons: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVELLER_BOOKING_ADDONS_MUTATION, variables)
  },

  updateBookingAddonsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        bookingAddonIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVELLER_BOOKING_ADDONS_ORDER_MUTATION, variables)
  },

  assignExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_TRAVELLER_EXTENSIONS_MUTATION, variables)
  },

  unassignExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_TRAVELLER_EXTENSIONS_MUTATION, variables)
  },

  createExtensions: async (id: string, items: Array<ratioTravelExtensionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensions: items,
      },
    }
    return graphQLClient.request(CREATE_TRAVELLER_EXTENSIONS_MUTATION, variables)
  },

  deleteExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_TRAVELLER_EXTENSIONS_MUTATION, variables)
  },

  updateExtensionsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_TRAVELLER_EXTENSIONS_ORDER_MUTATION, variables)
  },
}

export default travellerRepository
